import type { Address } from "viem"

import type { LcAddress } from "@/domains/core"
import type { MinimalPoolRepository, MinimalPoolStore } from "@/domains/minimal-pool"
import type { ManualClPoolRepository as ThickV1PoolRepository } from "@/domains/thick-v1/manual-cl-repository"
import type { ManualClPoolStore as ThickV1PoolStore } from "@/domains/thick-v1/manual-cl-store"
import type { ManualClPoolRepository as ThickV2PoolRepository } from "@/domains/thick-v2/manual-cl-repository"
import type { ManualClPoolStore as ThickV2PoolStore } from "@/domains/thick-v2/manual-cl-store"
import type { TokenStore } from "@/domains/tokens"

export type ScaleRouteHop = {
  from: Address
  to: Address
  stable: boolean
  poolAddress: Address
  poolName: string
}

export type ScaleClassicRouteCandidate = {
  source: "classic-vamm-samm"
  id: string
  hops: ScaleRouteHop[]
  pools: MinimalPoolStore[]
}

export type ScaleConcentratedLiquidityRouteCandidate = {
  source: "thick-v1" | "thick-v2"
  id: string
  hops: ScaleRouteHop[]
  pools: []
  clPool: {
    address: Address
    token0: TokenStore
    token1: TokenStore
    feeTier: number
    tickSpacing?: number
    tvlUsd: number
    liquidity: bigint
  }
}

export type ScaleRouteCandidate = ScaleClassicRouteCandidate | ScaleConcentratedLiquidityRouteCandidate

export interface IScaleRouteFinder {
  findRoutes(inputToken: TokenStore, outputToken: TokenStore): ScaleRouteCandidate[]
}

type ScaleRouteFinderParams = {
  getPoolRepository: () => MinimalPoolRepository | undefined
  getThickV1PoolRepository?: () => ThickV1PoolRepository | undefined
  getThickV2PoolRepository?: () => ThickV2PoolRepository | undefined
  hubTokens: () => TokenStore[]
  maxRoutes?: number
}

export class ScaleRouteFinder implements IScaleRouteFinder {
  private readonly maxRoutes: number

  constructor(private readonly params: ScaleRouteFinderParams) {
    this.maxRoutes = params.maxRoutes ?? 18
  }

  findRoutes(inputToken: TokenStore, outputToken: TokenStore): ScaleRouteCandidate[] {
    if (inputToken.address.equals(outputToken.address)) {
      return []
    }

    const repository = this.params.getPoolRepository()
    const directRoutes = repository ? this.findDirectRoutes(repository, inputToken.address, outputToken.address) : []
    const twoHopRoutes = repository ? this.findTwoHopRoutes(repository, inputToken, outputToken) : []
    const threeHopRoutes = repository ? this.findThreeHopRoutes(repository, inputToken, outputToken) : []
    const thickV1Routes = this.findThickV1DirectRoutes(inputToken, outputToken)
    const thickV2Routes = this.findThickV2DirectRoutes(inputToken, outputToken)

    return this.dedupeRoutes([...directRoutes, ...twoHopRoutes, ...threeHopRoutes, ...thickV1Routes, ...thickV2Routes])
      .sort((a, b) => this.routeScore(b) - this.routeScore(a))
      .slice(0, this.maxRoutes)
  }

  private findDirectRoutes(
    repository: MinimalPoolRepository,
    inputAddress: LcAddress,
    outputAddress: LcAddress,
  ): ScaleRouteCandidate[] {
    return repository
      .findActivePoolsByTokenPair(inputAddress, outputAddress)
      .filter((pool) => this.hasUsableLiquidity(pool))
      .map((pool) => this.createRoute([pool], inputAddress, outputAddress))
  }

  private findTwoHopRoutes(
    repository: MinimalPoolRepository,
    inputToken: TokenStore,
    outputToken: TokenStore,
  ): ScaleRouteCandidate[] {
    return repository
      .findActivePoolsByToken(inputToken.address)
      .filter((pool) => this.hasUsableLiquidity(pool))
      .flatMap((firstLeg) => {
        const hubToken = firstLeg.token0.address.equals(inputToken.address) ? firstLeg.token1 : firstLeg.token0
        if (hubToken.address.equals(outputToken.address)) {
          return []
        }
        const secondLegs = repository.findActivePoolsByTokenPair(hubToken.address, outputToken.address)
        return this.combinePoolLegs([[firstLeg], secondLegs], inputToken.address, outputToken.address)
      })
  }

  private findThreeHopRoutes(
    repository: MinimalPoolRepository,
    inputToken: TokenStore,
    outputToken: TokenStore,
  ): ScaleRouteCandidate[] {
    const hubs = this.params
      .hubTokens()
      .filter((hubToken) => !hubToken.address.equals(inputToken.address) && !hubToken.address.equals(outputToken.address))

    const routes: ScaleRouteCandidate[] = []

    for (const firstHub of hubs) {
      for (const secondHub of hubs) {
        if (firstHub.address.equals(secondHub.address)) continue

        const firstLegs = repository.findActivePoolsByTokenPair(inputToken.address, firstHub.address)
        const secondLegs = repository.findActivePoolsByTokenPair(firstHub.address, secondHub.address)
        const thirdLegs = repository.findActivePoolsByTokenPair(secondHub.address, outputToken.address)
        routes.push(...this.combinePoolLegs([firstLegs, secondLegs, thirdLegs], inputToken.address, outputToken.address))
      }
    }

    return routes
  }

  private combinePoolLegs(
    legOptions: MinimalPoolStore[][],
    inputAddress: LcAddress,
    outputAddress: LcAddress,
  ): ScaleRouteCandidate[] {
    if (legOptions.some((legs) => legs.length === 0)) {
      return []
    }

    const usableLegOptions = legOptions.map((legs) => legs.filter((pool) => this.hasUsableLiquidity(pool)).slice(0, 2))
    if (usableLegOptions.some((legs) => legs.length === 0)) {
      return []
    }

    return usableLegOptions.reduce<MinimalPoolStore[][]>(
      (routes, legs) => routes.flatMap((route) => legs.map((leg) => [...route, leg])),
      [[]],
    ).map((pools) => this.createRoute(pools, inputAddress, outputAddress))
  }

  private findThickV1DirectRoutes(
    inputToken: TokenStore,
    outputToken: TokenStore,
  ): ScaleConcentratedLiquidityRouteCandidate[] {
    const repository = this.params.getThickV1PoolRepository?.()
    if (!repository) return []

    return repository
      .getActivePools()
      .filter((pool) => this.isPoolForTokenPair(pool, inputToken, outputToken))
      .filter((pool) => this.hasUsableClLiquidity(pool))
      .map((pool) => this.createClRoute("thick-v1", pool, inputToken, outputToken))
  }

  private findThickV2DirectRoutes(
    inputToken: TokenStore,
    outputToken: TokenStore,
  ): ScaleConcentratedLiquidityRouteCandidate[] {
    const repository = this.params.getThickV2PoolRepository?.()
    if (!repository) return []

    return repository
      .getActivePools()
      .filter((pool) => this.isPoolForTokenPair(pool, inputToken, outputToken))
      .filter((pool) => this.hasUsableClLiquidity(pool))
      .map((pool) => this.createClRoute("thick-v2", pool, inputToken, outputToken))
  }

  private createRoute(
    pools: MinimalPoolStore[],
    inputAddress: LcAddress,
    outputAddress: LcAddress,
  ): ScaleClassicRouteCandidate {
    const hops: ScaleRouteHop[] = []
    let currentAddress = inputAddress

    for (const pool of pools) {
      const nextToken = pool.token0.address.equals(currentAddress) ? pool.token1 : pool.token0
      hops.push({
        from: currentAddress.toViemAddress(),
        to: nextToken.address.toViemAddress(),
        stable: pool.isStable,
        poolAddress: pool.address.toViemAddress(),
        poolName: pool.symbol,
      })
      currentAddress = nextToken.address
    }

    const id = hops.map((hop) => `${hop.from}-${hop.to}-${hop.stable ? "stable" : "volatile"}`).join("|")
    const validRoute = currentAddress.equals(outputAddress)

    return {
      source: "classic-vamm-samm",
      id: validRoute ? id : `${id}|invalid`,
      hops,
      pools,
    }
  }

  private createClRoute(
    source: "thick-v1" | "thick-v2",
    pool: ThickV1PoolStore | ThickV2PoolStore,
    inputToken: TokenStore,
    outputToken: TokenStore,
  ): ScaleConcentratedLiquidityRouteCandidate {
    const id = `${source}-${pool.address.toString()}-${inputToken.address.toString()}-${outputToken.address.toString()}`
    return {
      source,
      id,
      hops: [
        {
          from: inputToken.address.toViemAddress(),
          to: outputToken.address.toViemAddress(),
          stable: false,
          poolAddress: pool.address.toViemAddress(),
          poolName: `${source}:${pool.displayName}`,
        },
      ],
      pools: [],
      clPool: {
        address: pool.address.toViemAddress(),
        token0: pool.token0,
        token1: pool.token1,
        feeTier: pool.feeTier,
        tickSpacing: "tickSpacing" in pool ? pool.tickSpacing : undefined,
        tvlUsd: pool.tvlUsd,
        liquidity: pool.liquidity,
      },
    }
  }

  private dedupeRoutes(routes: ScaleRouteCandidate[]): ScaleRouteCandidate[] {
    const uniqueRoutes = new Map<string, ScaleRouteCandidate>()
    for (const route of routes) {
      if (route.id.endsWith("|invalid")) continue
      uniqueRoutes.set(route.id, route)
    }
    return Array.from(uniqueRoutes.values())
  }

  private routeScore(route: ScaleRouteCandidate): number {
    if (route.source === "thick-v1" || route.source === "thick-v2") {
      return route.clPool.tvlUsd - 1_000_000
    }

    const tvlScore = route.pools.reduce((total, pool) => total + pool.tvlUsd, 0)
    const hopPenalty = route.hops.length * 1_000_000
    return tvlScore - hopPenalty
  }

  private hasUsableLiquidity(pool: MinimalPoolStore): boolean {
    return pool.reserve0 > 0n && pool.reserve1 > 0n
  }

  private hasUsableClLiquidity(pool: ThickV1PoolStore | ThickV2PoolStore): boolean {
    return pool.liquidity > 0n && pool.tvlUsd > 0
  }

  private isPoolForTokenPair(
    pool: ThickV1PoolStore | ThickV2PoolStore,
    inputToken: TokenStore,
    outputToken: TokenStore,
  ): boolean {
    return (
      (pool.token0.address.equals(inputToken.address) && pool.token1.address.equals(outputToken.address)) ||
      (pool.token0.address.equals(outputToken.address) && pool.token1.address.equals(inputToken.address))
    )
  }
}
